<template>
  <div class="classifier-filters-container" :style="{ flexDirection: displayDirection }">
    <slot class="select-filter" name="customFilters" />

    <SelectFilter
      v-for="classifier in classifiersBasedOnExperience"
      :key="classifier.id"
      :name="`classifier-${classifier.id}`"
      :class="classifierClasses(classifier)"
      :hide-if-empty="true"
    />
  </div>
</template>

<script lang="ts">
import SelectFilter from '@components/filters/SelectFilter.vue'
import { ClassifierInterface } from '@lib/classifierUtils/types'
import { isCoreClassifiersEnabled } from '@lib/classifierUtils/isCoreClassifiersEnabled'
import { orderClassifiersByType } from '@lib/classifierUtils/orderClassifiersByType'
import { removeCoreClassifiers } from '@lib/classifierUtils/removeCoreClassifiers'
import { isCoreClassifier } from '@lib/classifierUtils/isCoreClassifier'
import { keepCoreClassifiers } from '@lib/classifierUtils/keepCoreClassifiers'

/**
 * ClassifierFilters
 *
 * Renders a dynamic set of `<SelectFilter>` components based on the classifiers available
 *
 * Constraints (docs of usage are below in the @example(s)):
 *
 * #1. The classifier values options must be initialized in the select filter store
 *     by the parent of <ClassifierFilters />
 *
 * #2. The selected options must be retrieved from the select filter store (to use
 *     in a request body, for example)
 *
 * ------------------------------------
 *
 * @component
 *
 * @property {ClassifierInterface[]} [classifiers=[]]
 *
 *  @property {boolean} [onlyCore=false]
 *   If `true`, only “core” classifiers will be displayed
 *
 * @property {string} [displayDirection='column']
 *   Flex direction: `'column'` or `'row'`
 *
 * @property {boolean} [highlightCore=false]
 *   If `true`, highlights the Core classifiers with a purple border
 *
 * @property {object} [account={}]
 *   The account that has the classifiers
 *
 * ------------------------------------
 * @usage
 *
 * ```vue
 * <ClassifierFilters :classifiers="myClassifiers" />
 * ...
 * <ClassifierFilters :classifiers="myClassifiers" onlyCore displayDirection="row" />
 * ...
 * <ClassifierFilters :classifiers="myClassifiers" />
 * ```
 *
 * ------------------------------------
 *
 * @example Initializing the select filter(s) options (handles the constraint #1):
 *
 * ```ts
 * // direct parent of <ClassifierFilters />
 *
 * ...
 * const classifierStore = useClassifierStore()
 * const selectFilterStore = useSelectFilterStore()
 * classifierStore.fetchClassifiers()
 * ...
 *
 * watch(() => {
 *   'classifierStore.classifiers': {
 *     handler() {
 *       classifierStore.classifiers.forEach(c =>
 *         changeSelectFilterStore({
 *           filterKey: `classifier-${c.id}`,
 *           groupingKey: 'classifier_value_ids',
 *           allOptionName: `${c.title}: all`,
 *
 *           //
 *           // using helper @lib/classifierUtils/rawOptionsFromFiltersResponse
 *           // to treat the 'classifier_filters' (which usually comes from the API)
 *           //
 *           rawOptions: rawOptionsFromFiltersResponse(c.id, data.classifier_filters),
 *         })
 *       )
 *     }
 *   }
 * }
 * ```
 *
 * @example Building the request body using the selected option(s) from the
 *          select filter store (handles the constraint #2):
 *
 * ```ts
 * //
 * // parent of <ClassifierFilters /> (or whatever needs to use the selected
 * // options from the select filter store)
 * //
 *
 * const selectFilterStore = useSelectFilterStore()
 *
 * const selectedClassifierValueIds = selectFilterStore
 *   .filters
 *   .filter(f => f.key.startsWith('classifier-'))
 *   .map(f => f.selectedOption).filter(Boolean).join(',')
 *
 * //
 * // requestBody = { ..., classifier_value_ids: selectedClassifierValueIds }
 * //
 * ```
 */
export default {
  name: 'ClassifierFilters',

  components: { SelectFilter },

  props: {
    classifiers: {
      type: Array<ClassifierInterface>,
      default: (): ClassifierInterface[] => [],
      required: true,
    },

    onlyCore: {
      type: Boolean,
      default: false,
      required: false,
    },

    displayDirection: {
      type: String,
      default: 'column',
      required: false,
    },

    highlightCore: {
      type: Boolean,
      default: false,
      required: false,
    },

    account: {
      type: Object,
      required: true,
    },
  },

  computed: {
    isCoreClassifiersEnabled(): boolean {
      return isCoreClassifiersEnabled(this.account)
    },

    notCoreClassifiers(): ClassifierInterface[] {
      return removeCoreClassifiers(this.classifiers || [])
    },

    orderClassifiersByType(): ClassifierInterface[] {
      return orderClassifiersByType(this.classifiers || [])
    },

    classifiersBasedOnExperience(): ClassifierInterface[] {
      if (this.isCoreClassifiersEnabled) {
        if (this.onlyCore) {
          return keepCoreClassifiers(this.classifiers || [])
        }

        return this.orderClassifiersByType
      } else {
        return this.notCoreClassifiers
      }
    },

    baseSelectFilterClassName() {
      return 'select-filter'
    },

    coreClassierFilterClassName() {
      return 'core-classifier'
    },
  },

  methods: {
    isCoreClassifier(classifier: ClassifierInterface): boolean {
      return isCoreClassifier(classifier.type)
    },
    classifierClasses(classifier: ClassifierInterface): string[] {
      let classes = [this.baseSelectFilterClassName]

      if (this.isCoreClassifiersEnabled && this.highlightCore) {
        if (this.isCoreClassifier(classifier)) {
          classes.push(this.coreClassierFilterClassName)
        }
      }

      return classes
    },
  },
}
</script>
<style lang="sass">
.classifier-filters-container
  display: flex
  gap: 1rem
  flex-wrap: wrap

  .select-filter

    &.core-classifier
      color: var(--uePurple) !important

      [id^="input-"]
        border: 1px solid var(--uePurple) !important

        svg
          & path
            stroke: var(--uePurple) !important
</style>
